import React, { useEffect, useRef, useState } from 'react';
import distributorService from '../../services/distributorService';
import { captureSession, isSessionCurrent } from '../../utils/authSession';
import { useT } from '../../i18n/LanguageContext';

const same = (a, b) =>
  a.allowedFamilyKeys.length === b.allowedFamilyKeys.length &&
  a.allowedFamilyKeys.every((key) => b.allowedFamilyKeys.includes(key));
const changesFor = (value) => ({ allowedFamilyKeys: [...value.allowedFamilyKeys] });
const readSettings = (response, id) => {
  const value = response?.data?.data;
  if (
    value?.distributorId !== id ||
    !Number.isSafeInteger(value.revision) ||
    value.revision < 0 ||
    typeof value.enforcementEnabled !== 'boolean' ||
    !Array.isArray(value.allowedFamilyKeys) ||
    !Array.isArray(value.supportedFamilies) ||
    value.supportedFamilies.some(
      (family) =>
        typeof family?.key !== 'string' ||
        !family.key ||
        typeof family.label !== 'string' ||
        !family.label.trim()
    ) ||
    new Set(value.supportedFamilies.map((family) => family.key)).size !==
      value.supportedFamilies.length ||
    new Set(value.allowedFamilyKeys).size !== value.allowedFamilyKeys.length ||
    value.allowedFamilyKeys.some(
      (key) => !value.supportedFamilies.some((family) => family.key === key)
    )
  )
    throw new Error('Invalid product access');
  return value;
};

export default function DistributorProductAccess({ distributor, disabled, onLockChange }) {
  const t = useT();
  const id = distributor._id;
  const [expanded, setExpanded] = useState(false);
  const [saved, setSaved] = useState(null);
  const [draft, setDraft] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uncertain, setUncertain] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const mounted = useRef(false);
  const session = useRef(captureSession());
  const target = useRef(id);
  const pending = useRef(false);
  const sequence = useRef(0);
  const current = () =>
    mounted.current && target.current === id && isSessionCurrent(session.current);
  const dirty = Boolean(saved && draft && !same(saved, draft));

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
      sequence.current += 1;
    };
  }, []);
  useEffect(() => {
    onLockChange(busy || dirty || uncertain);
    const warn = (event) => {
      if (busy || dirty || uncertain) {
        event.preventDefault();
        event.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', warn);
    return () => {
      onLockChange(false);
      window.removeEventListener('beforeunload', warn);
    };
  }, [busy, dirty, uncertain, onLockChange]);

  const reload = async () => {
    if (!current() || pending.current || disabled || distributor.isActive !== true) return;
    pending.current = true;
    const generation = ++sequence.current;
    setBusy(true);
    setExpanded(true);
    setError('');
    setNotice('');
    try {
      const response = await distributorService.adminProductAccess(id);
      if (!current() || generation !== sequence.current) return;
      const value = readSettings(response, id);
      setSaved(value);
      setDraft(changesFor(value));
      setUncertain(false);
    } catch {
      if (current() && generation === sequence.current)
        setError('Product access could not load. Please retry.');
    } finally {
      pending.current = false;
      if (current() && generation === sequence.current) setBusy(false);
    }
  };
  const save = async (event) => {
    event.preventDefault();
    if (
      !current() ||
      disabled ||
      distributor.isActive !== true ||
      pending.current ||
      uncertain ||
      !dirty
    )
      return;
    const changes = changesFor(draft);
    pending.current = true;
    setBusy(true);
    setError('');
    setNotice('');
    try {
      const value = readSettings(
        await distributorService.saveAdminProductAccess(id, {
          revision: saved.revision,
          allowedFamilyKeys: changes.allowedFamilyKeys,
        }),
        id
      );
      if (!current()) return;
      if (!same(value, changes) || value.revision <= saved.revision)
        throw new Error('Unconfirmed product access');
      setSaved(value);
      setDraft(changesFor(value));
      setNotice('Product access saved.');
    } catch (failure) {
      if (!current()) return;
      setUncertain(true);
      if (failure?.response?.status === 409) {
        setError(
          'Product access changed. Your edits are still here. Discard and reload before saving again.'
        );
      } else {
        try {
          const value = readSettings(await distributorService.adminProductAccess(id), id);
          if (!current()) return;
          if (same(value, changes) && value.revision > saved.revision) {
            setSaved(value);
            setDraft(changesFor(value));
            setUncertain(false);
            setNotice('Product access saved.');
          } else {
            setError(
              'Product access could not be confirmed. Your edits are still here. Discard and reload before saving again.'
            );
          }
        } catch {
          if (current())
            setError(
              'Product access could not be confirmed. Your edits are still here. Discard and reload before saving again.'
            );
        }
      }
    } finally {
      pending.current = false;
      if (current()) setBusy(false);
    }
  };
  const discard = () => {
    if (!current() || disabled || pending.current) return;
    if (distributor.isActive !== true) {
      setDraft(changesFor(saved));
      setUncertain(false);
      setError('');
      return;
    }
    reload();
  };
  return (
    <section
      aria-label={t('Distributor product access')}
      className="me-distributor-admin-card space-y-4"
    >
      <h2 className="text-lg font-semibold">
        {t('Product access for {name}', { name: distributor.name })}
      </h2>
      <p className="text-sm text-slate-600">
        {t(
          'Choose the product families this distributor may offer. Customer selections can only narrow this list.'
        )}
      </p>
      {distributor.isActive !== true && (
        <p role="status">{t('This distributor is inactive. Product access is read-only.')}</p>
      )}
      {!expanded && (
        <button
          type="button"
          disabled={disabled || distributor.isActive !== true}
          onClick={reload}
          className="me-distributor-admin-secondary"
        >
          {t('Configure product access')}
        </button>
      )}
      {error && (
        <p role="alert" className="text-red-700">
          {t(error)}
        </p>
      )}
      {notice && (
        <p role="status" className="text-green-700">
          {t(notice)}
        </p>
      )}
      {busy && <p role="status">{t('Updating product access...')}</p>}
      {expanded && !draft && !busy && (
        <button
          type="button"
          disabled={disabled}
          onClick={reload}
          className="me-distributor-admin-secondary"
        >
          {t('Retry')}
        </button>
      )}
      {draft && (
        <form onSubmit={save} className="space-y-4">
          <p role="status" className="text-sm text-slate-600">
            {t(
              saved.enforcementEnabled
                ? 'Product access restrictions are active.'
                : 'Enforcement is not enabled. Saved selections are staged and do not restrict access yet.'
            )}
          </p>
          <fieldset
            disabled={disabled || busy || uncertain || distributor.isActive !== true}
            className="space-y-2"
          >
            <legend className="font-medium">{t('Allowed product families')}</legend>
            {saved.supportedFamilies.map((family) => (
              <label key={family.key} className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={draft.allowedFamilyKeys.includes(family.key)}
                  onChange={(event) => {
                    const checked = event.target.checked;
                    setDraft((value) => ({
                      allowedFamilyKeys: checked
                        ? [...value.allowedFamilyKeys, family.key]
                        : value.allowedFamilyKeys.filter((key) => key !== family.key),
                    }));
                    setNotice('');
                  }}
                />
                {family.label}
              </label>
            ))}
            {draft.allowedFamilyKeys.length === 0 && (
              <p className="text-sm text-amber-800">
                {t(
                  'No families selected. When enforcement is enabled, this blocks new purchases and orders; existing history and balances remain.'
                )}
              </p>
            )}
            <p className="text-sm text-slate-600">
              {t(
                'Only supported MedEffects product families are listed. Adding a catalog product does not automatically grant distributor access.'
              )}
            </p>
          </fieldset>
          <div className="flex flex-wrap gap-3">
            <button
              type="submit"
              disabled={disabled || busy || uncertain || !dirty || distributor.isActive !== true}
              className="me-distributor-admin-primary"
            >
              {t('Save product access')}
            </button>
            {(dirty || uncertain) && (
              <button
                type="button"
                disabled={disabled || busy}
                onClick={discard}
                className="me-distributor-admin-secondary"
              >
                {t(distributor.isActive === true ? 'Discard and reload' : 'Discard changes')}
              </button>
            )}
          </div>
        </form>
      )}
    </section>
  );
}
